import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EncrDecrService } from '@mysrc/app/Providers/EncrDecrService';
import { API_ENDPOINTS } from '@mysrc/app/api-endpoints';
import { BaseHttpReqRespService } from '@mysrc/app/modules/fwcore/services/base-http-req-resp.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { CommonService } from '../../question/services/commonservice';
import {
  ServiceLine,
  ManageServiceLineViewModel,
  Link,
  LinkDetails,
  ClientAdmin
} from '../models/service-line.model';

@Injectable()
export class SurveyServiceLineService extends BaseHttpReqRespService {
  myAppUrl: any;
  SL_ENDPOINT = API_ENDPOINTS.SURVEY_SERVICE_LINE;

  constructor(
    http: HttpClient,
    @Inject(OKTA_AUTH) private oauth: OktaAuth,
    private EncrDecr: EncrDecrService,
    private commonService: CommonService,
    @Inject('CONFIG_SETTINGS') configSettings: any
  ) {
    super(http, oauth);
    const genSettings = configSettings[<any>'general'];
    this.baseUrl = genSettings[<any>'adminApiUrl'];
    this.apiKey = genSettings[<any>'apiKey'];
    this.myAppUrl = this.baseUrl;
  }

  // ── GET: Service line list ─────────────────────────────────
  getServiceLineList(workspaceId: string): Observable<ServiceLine[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.LIST}?workspaceId=${workspaceId}`
    );
  }

  // ── GET: Edit service line ─────────────────────────────────
  editServiceLine(serviceLineId: number): Observable<ServiceLine> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.EDIT}?serviceLineId=${serviceLineId}`
    );
  }

  // ── POST: Save service line ────────────────────────────────
  saveServiceLine(model: Partial<ServiceLine>): Observable<ServiceLine[]> {
    const userId = this.EncrDecr.get(localStorage.getItem('userId') ?? '');
    const workSpaceId = this.EncrDecr.get(localStorage.getItem('workspaceId') ?? '');
    return this.http.post<ServiceLine[]>(
      `${this.myAppUrl}/${this.SL_ENDPOINT.SAVE}`,
      model,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params: { userId, serviceLineId: model.serviceLineId ?? 0, workSpaceId }
      }
    );
  }

  // ── POST: Delete service line ──────────────────────────────
  deleteServiceLine(serviceLineId: number): Observable<ServiceLine> {
    const workSpaceId = this.EncrDecr.get(localStorage.getItem('workspaceId') ?? '');
    return this.http.post<ServiceLine>(
      `${this.myAppUrl}/${this.SL_ENDPOINT.DELETE}?serviceLineId=${serviceLineId}&workSpaceId=${workSpaceId}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── GET: Mapped service provider list ──────────────────────
  getMappedServiceProviderList(serviceLineId: number): Observable<ManageServiceLineViewModel[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.MAPPED_SERVICE_PROVIDERS}?serviceLineId=${serviceLineId}`
    );
  }

  // ── GET: Mapped survey list ────────────────────────────────
  getMappedSurveyList(serviceLineId: number, companyId: string): Observable<ManageServiceLineViewModel[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.MAPPED_SURVEYS}?serviceLineId=${serviceLineId}&companyId=${companyId}`
    );
  }

  // ── GET: Link list ─────────────────────────────────────────
  getLinkList(): Observable<Link[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.LINK_LIST}`
    );
  }

  // ── GET: Mapped link details ───────────────────────────────
  getMappedLink(linkId: number): Observable<LinkDetails> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.MAPPED_LINK}?linkId=${linkId}`
    );
  }

  // ── POST: Save link mapping ────────────────────────────────
  saveLink(model: LinkDetails): Observable<string> {
    return this.http.post<string>(
      `${this.myAppUrl}/${this.SL_ENDPOINT.SAVE_LINK}`,
      model,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    );
  }

  // ── GET: Client admin list ─────────────────────────────────
  getClientAdminList(companyId: number): Observable<ClientAdmin[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.SL_ENDPOINT.CLIENT_ADMIN_LIST}?companyId=${companyId}`
    );
  }
}
